'use client'

import React from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { User, Calendar, MoreHorizontal, Trash2 } from 'lucide-react'
import { useTranslations } from '@/lib/i18n'
import Link from 'next/link'
import { getTrackerBadgeStyle, getStatusBadgeStyle, getPriorityBadgeStyle } from '@/lib/badge-utils'

interface Issue {
  id: number
  number?: number
  subject: string
  description?: string
  tracker?: {
    id: number
    name: string
  }
  status?: {
    id: number
    name: string
    is_closed: boolean
  }
  priority?: {
    id: number
    name: string
  }
  author?: {
    id: number
    name: string
  }
  assigned_to?: {
    id: number
    name: string
  }
  category?: {
    id: number
    name: string
  }
  fixed_version?: {
    id: number
    name: string
  }
  parent?: {
    id: number
    subject: string
  }
  children?: Issue[]
  start_date?: string
  due_date?: string
  done_ratio?: number
  estimated_hours?: number
  spent_hours?: number
  created_on?: string
  updated_on?: string
  custom_fields?: Array<{
    id: number
    name: string
    value: string
  }>
  project?: {
    id: number
    name: string
  }
}

interface IssueCardGridProps {
  issue: Issue
  onOpenMenu?: (issue: Issue) => void
  onDelete?: (issue: Issue) => void
  showProjectName?: boolean
}

const getPriorityIcon = (priority: string) => {
  const icons: Record<string, JSX.Element> = {
    'Low': <span className="text-green-600">↓</span>,
    'Normal': <span className="text-blue-600">−</span>,
    'High': <span className="text-orange-600">↑</span>,
    'Urgent': <span className="text-red-600">↑</span>,
    'Immediate': <span className="text-purple-600">↑</span>
  }
  return icons[priority] || <span className="text-gray-600">−</span>
}

const IssueCardGridComponent = ({ issue, onOpenMenu, onDelete, showProjectName }: IssueCardGridProps) => {
  const t = useTranslations('issuesPage')
  return (
    <Card className="hover:shadow-sm transition-shadow border border-border h-[320px] flex flex-col">
      <CardContent className="p-0 flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <Link
            href={`/issues/${issue.id}`}
            className="font-semibold text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            #{issue.number ?? issue.id}
          </Link>
          <div className="flex items-center gap-0.5 shrink-0">
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(issue) }}
                className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                title={t('deleteIssue')}
                aria-label={t('deleteIssue')}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
            {onOpenMenu && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onOpenMenu(issue)}
                className="h-6 w-6 p-0"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Title */}
        <div className="px-4 pb-2">
          <Link
            href={`/issues/${issue.id}`}
            className="block"
          >
            <h3 className="font-medium text-sm text-foreground hover:text-primary transition-colors line-clamp-2 leading-5 h-10">
              {issue.subject}
            </h3>
            {/* Project name under the title */}
            { (typeof showProjectName === 'undefined' || showProjectName) && (
              <div className="text-xs text-muted-foreground mt-1 truncate">{issue.project?.name}</div>
            ) }
          </Link>
        </div>

        {/* Progress Bar */}
        <div className="px-4 pb-3">
          {(issue.done_ratio || 0) > 0 ? (
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-foreground rounded-full transition-all"
                  style={{ width: `${issue.done_ratio || 0}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground w-8 text-right shrink-0">{issue.done_ratio}%</span>
            </div>
          ) : (
            <div className="h-[22px]"></div>
          )}
        </div>

        {/* Tags Row */}
        <div className="px-4 pb-3">
          <div className="flex items-center gap-1.5 h-5 overflow-hidden">
            {issue.tracker && (
              <Badge
                variant={getTrackerBadgeStyle(issue.tracker.name).variant}
                className={`text-[10px] font-normal px-1.5 py-0 h-5 shrink-0 ${getTrackerBadgeStyle(issue.tracker.name).className}`}
              >
                {issue.tracker.name}
              </Badge>
            )}
            {issue.status && (
              <Badge
                variant={getStatusBadgeStyle(issue.status.name, issue.status.is_closed).variant}
                className={`text-[10px] font-normal px-1.5 py-0 h-5 shrink-0 whitespace-nowrap ${getStatusBadgeStyle(issue.status.name, issue.status.is_closed).className}`}
              >
                {issue.status.name}
              </Badge>
            )}
            {issue.priority && (
              <Badge
                variant={getPriorityBadgeStyle(issue.priority.id, issue.priority.name).variant}
                className={`text-[10px] font-normal px-1.5 py-0 h-5 shrink-0 flex items-center gap-0.5 ${getPriorityBadgeStyle(issue.priority.id, issue.priority.name).className}`}
              >
                <span className="scale-75">{getPriorityIcon(issue.priority.name)}</span>
              </Badge>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="px-4 pb-4">
          <div className="h-16 flex items-start">
            {issue.description ? (
              <p className="text-xs text-muted-foreground line-clamp-4 leading-4">
                {issue.description}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground/50 italic">
                {t('noDescription')}
              </p>
            )}
          </div>
        </div>

        {/* Spacer to push footer to bottom */}
        <div className="flex-1"></div>

        {/* Divider */}
        <div className="border-t border-border"></div>

        {/* Footer - Name and Date side by side */}
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
              <User className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate whitespace-nowrap">{issue.assigned_to?.name || t('unassigned')}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
              <Calendar className="h-3.5 w-3.5" />
              <span>{issue.updated_on ? new Date(issue.updated_on).toLocaleDateString() : t('notAvailable')}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Custom comparison function for grid view
const arePropsEqual = (prevProps: IssueCardGridProps, nextProps: IssueCardGridProps) => {
  // Quick reference check
  if (prevProps.issue === nextProps.issue) {
    return (
      prevProps.showProjectName === nextProps.showProjectName &&
      prevProps.onOpenMenu === nextProps.onOpenMenu
    )
  }

  const prevIssue = prevProps.issue
  const nextIssue = nextProps.issue

  return (
    prevIssue.id === nextIssue.id &&
    prevIssue.subject === nextIssue.subject &&
    prevIssue.done_ratio === nextIssue.done_ratio &&
    prevIssue.updated_on === nextIssue.updated_on &&
    prevIssue.tracker?.id === nextIssue.tracker?.id &&
    prevIssue.tracker?.name === nextIssue.tracker?.name &&
    prevIssue.status?.id === nextIssue.status?.id &&
    prevIssue.status?.name === nextIssue.status?.name &&
    prevIssue.priority?.id === nextIssue.priority?.id &&
    prevIssue.priority?.name === nextIssue.priority?.name &&
    prevIssue.assigned_to?.id === nextIssue.assigned_to?.id &&
    prevIssue.assigned_to?.name === nextIssue.assigned_to?.name &&
    prevIssue.project?.id === nextIssue.project?.id &&
    prevIssue.project?.name === nextIssue.project?.name &&
    prevProps.showProjectName === nextProps.showProjectName &&
    prevProps.onOpenMenu === nextProps.onOpenMenu
  )
}

// Export memoized component
export const IssueCardGrid = React.memo(IssueCardGridComponent, arePropsEqual)
